"use client";

import { c as _c } from "react/compiler-runtime";
import { ENTP_PA_MEDICATION_ASSIGNMENT, ENTP_PA_PROCEDURE_ASSIGNMENT, hasEntpOfficeVisitCptCodes } from "@/configuration/groups/entpartners/configuration";
import { ENTP_CHICAGO_ECW_LOGIN_URL } from "@/configuration/groups/entpartners/referral-instructions";
import { CopyableContent } from "@/src/components/copyable-content";
import { entpCptToStaff } from "@/src/data/entp-cpt-staff-mapping";
import { generatePriorAuthFilename } from "@/src/lib/utils/filename";
import { PriorAuthorizationStatus, PriorAuthorizationType } from "@/src/models/documents/prior-authorization/prior-authorization";
import { Task } from "@/src/models/task";
type StaffAssignment = Record<string, string>;
const DEFAULT_NO_ASSIGNEE = "No Assignee found for CPT";
const listClass = "list-disc ml-3 flex flex-col gap-0.5";
function getNameInstructions(status: PriorAuthorizationStatus, type: PriorAuthorizationType | null, numberOfServices: number | null | undefined, date: string | null | undefined) {
  return <>
      <p>eCW:</p>
      <a href={ENTP_CHICAGO_ECW_LOGIN_URL} target="_blank">
        {ENTP_CHICAGO_ECW_LOGIN_URL}
      </a>
      <p>Go to the patient&apos;s documents and select the &quot;Authorization folder&quot;.</p>
      <ul className={listClass}>
        <li>
          <CopyableContent text={generatePriorAuthFilename(null, date ?? null, status, type, numberOfServices ?? null)} />
        </li>
        <li>
          The date should be the date of the payer update, i.e. the sent date at the top of the letterhead. If it&apos;s
          not clear or the document does not have a date, then put the approved date.
        </li>
        {type !== PriorAuthorizationType.MEDICATION ? <li>If the approval is for a number of visits, add a number, e.g. 2025.02.20 Auth Approved 12 visits</li> : null}
      </ul>
    </>;
}
function getStaffAssignment(staffAssignment: StaffAssignment) {
  return <ul className={listClass}>
      {Object.keys(staffAssignment).length > 0 ? Object.entries(staffAssignment).map(([cptCode, staff], idx) => <li key={idx}>
            {cptCode} → <CopyableContent text={staff} />
          </li>) : <li>No CPTs found</li>}
    </ul>;
}
export function PriorAuthorizationInstruction(t0) {
  const $ = _c(33);
  const {
    task
  } = t0;
  const priorAuth = task.priorAuthorization;
  const status = priorAuth?.authorizationStatus ?? PriorAuthorizationStatus.OTHER;
  const type = priorAuth?.authorizationType ?? null;
  const provider = task.priorAuthorization?.practitioner;
  let t1;
  if ($[0] !== provider?.familyName) {
    t1 = provider?.familyName?.toLowerCase();
    $[0] = provider?.familyName;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const providerFamilyName = t1;
  let t2;
  if ($[2] !== priorAuth?.commonProcedureTerminologyList || $[3] !== provider?.familyName || $[4] !== providerFamilyName || $[5] !== status || $[6] !== type) {
    const cptCodes = priorAuth?.commonProcedureTerminologyList?.map(_temp) ?? [];
    const staffAssignment = cptCodes.reduce(_temp2, {});
    let t3;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <p>Assignment Depends on type of medication:</p>;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    let t4;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <li>Allergy = <CopyableContent text="Cuzco, Janice" />{" "}</li>;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    let t5;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <li>ENT = <CopyableContent text="User, Triage" />{" "}</li>;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    let t6;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <li>Sleep = <CopyableContent text="Nikolayeva, Nina" />{" "}</li>;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    let t7;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <li>Audiology = <CopyableContent text="Gamez, Emily" />{" "}</li>;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    let t8;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <>{t3}<ul className={listClass}>{t4}{t5}{t6}{t7}<li>SubOto = <CopyableContent text="Sub Sanchez, Rafaela" />{" "}</li></ul></>;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    const noProviderMedicationAssignment = t8;
    let t9;
    if ($[14] !== providerFamilyName) {
      t9 = providerFamilyName ? <CopyableContent text={ENTP_PA_MEDICATION_ASSIGNMENT[providerFamilyName] || "User, Triage"} /> : noProviderMedicationAssignment;
      $[14] = providerFamilyName;
      $[15] = t9;
    } else {
      t9 = $[15];
    }
    const medicationAssignment = t9;
    const t10 = provider?.familyName;
    let t11;
    if ($[16] !== medicationAssignment || $[17] !== t10) {
      t11 = <><p>Provider is {t10} assign to {medicationAssignment}</p></>;
      $[16] = medicationAssignment;
      $[17] = t10;
      $[18] = t11;
    } else {
      t11 = $[18];
    }
    const medicationDispositionInstructions = t11;
    const procedureAssignment = providerFamilyName && providerFamilyName in ENTP_PA_PROCEDURE_ASSIGNMENT ? <CopyableContent text={ENTP_PA_PROCEDURE_ASSIGNMENT[providerFamilyName]} /> : getStaffAssignment(staffAssignment);
    const procedureAssignmentInstructions = <><p>Provider is {provider?.familyName} assign to {procedureAssignment}</p></>;
    bb0: {
      if (hasEntpOfficeVisitCptCodes(cptCodes)) {
        if (status === PriorAuthorizationStatus.APPROVED) {
          let t12;
          if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
            t12 = <p>Mark the Document as Reviewed</p>;
            $[19] = t12;
          } else {
            t12 = $[19];
          }
          t2 = t12;
          break bb0;
        }
        if (status === PriorAuthorizationStatus.DENIED) {
          const t12 = provider?.familyName;
          let t13;
          if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
            t13 = <CopyableContent text="User, Scheduler" />;
            $[20] = t13;
          } else {
            t13 = $[20];
          }
          let t14;
          if ($[21] !== t12) {
            t14 = <p>Provider is {t12} assign to {t13}</p>;
            $[21] = t12;
            $[22] = t14;
          } else {
            t14 = $[22];
          }
          t2 = t14;
          break bb0;
        }
      }
      if (type === PriorAuthorizationType.PROCEDURE) {
        if (status === PriorAuthorizationStatus.APPROVED) {
          let t12;
          if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
            t12 = <p>Mark the Document as Reviewed</p>;
            $[23] = t12;
          } else {
            t12 = $[23];
          }
          t2 = t12;
          break bb0;
        }
        let t12;
        if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
          t12 = <p>Assign to <CopyableContent text="Grant, Laura" /></p>;
          $[24] = t12;
        } else {
          t12 = $[24];
        }
        t2 = t12;
        break bb0;
      }
      if (type === PriorAuthorizationType.MEDICATION) {
        t2 = medicationDispositionInstructions;
        break bb0;
      }
      t2 = procedureAssignmentInstructions;
    }
    $[2] = priorAuth?.commonProcedureTerminologyList;
    $[3] = provider?.familyName;
    $[4] = providerFamilyName;
    $[5] = status;
    $[6] = type;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const dispositionInstructions = t2;
  const t3 = priorAuth?.numberOfServices;
  const t4 = priorAuth?.dateOfDocument;
  let t5;
  if ($[25] !== status || $[26] !== t3 || $[27] !== t4 || $[28] !== type) {
    t5 = getNameInstructions(status, type, t3, t4);
    $[25] = status;
    $[26] = t3;
    $[27] = t4;
    $[28] = type;
    $[29] = t5;
  } else {
    t5 = $[29];
  }
  let t6;
  if ($[30] !== dispositionInstructions || $[31] !== t5) {
    t6 = <div className="flex flex-col gap-2">{t5}{dispositionInstructions}</div>;
    $[30] = dispositionInstructions;
    $[31] = t5;
    $[32] = t6;
  } else {
    t6 = $[32];
  }
  return t6;
}
function _temp2(acc, cptCode) {
  return {
    ...acc,
    [cptCode]: entpCptToStaff[cptCode] || DEFAULT_NO_ASSIGNEE
  };
}
function _temp(cpt) {
  return cpt.code;
}